import type { GameState } from "../domain/types";

const treasureTypes = new Set(["coin", "treasure", "gem", "relic", "relicFragment"]);

export type SolvabilityReport = {
  solvable: boolean;
  exitReachable: boolean;
  reachableTreasures: number;
  reachable: number[];
};

export function validateSolvability(state: GameState): SolvabilityReport {
  const queue = [state.playerIndex];
  const seen = new Set(queue);
  while (queue.length) {
    const current = queue.shift()!;
    const row = Math.floor(current / state.size);
    const column = current % state.size;
    for (const [dy, dx] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const y = row + dy, x = column + dx;
      if (y < 0 || y >= state.size || x < 0 || x >= state.size) continue;
      const next = y * state.size + x;
      if (!seen.has(next) && state.grid[next].walkable) {
        seen.add(next);
        queue.push(next);
      }
    }
  }
  const reachable = [...seen];
  const reachableTreasures = reachable.filter(index => treasureTypes.has(state.grid[index].type)).length;
  const exitReachable = seen.has(state.exitIndex);
  return {
    solvable: exitReachable && reachableTreasures >= state.treasureGoal,
    exitReachable,
    reachableTreasures,
    reachable,
  };
}

export function shortestPath(state: GameState, target = state.exitIndex): number[] {
  const queue = [state.playerIndex];
  const parent = new Map<number, number>();
  const seen = new Set(queue);
  while (queue.length) {
    const current = queue.shift()!;
    if (current === target) {
      const path = [current];
      while (parent.has(path[0])) path.unshift(parent.get(path[0])!);
      return path;
    }
    const row = Math.floor(current / state.size);
    const column = current % state.size;
    for (const [dy, dx] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const y = row + dy, x = column + dx;
      if (y < 0 || y >= state.size || x < 0 || x >= state.size) continue;
      const next = y * state.size + x;
      if (!seen.has(next) && state.grid[next].walkable) {
        seen.add(next); parent.set(next, current); queue.push(next);
      }
    }
  }
  return [];
}
